import { API_URL, authenticatedFetch } from "../config/api";

export const getFloorsBySiteId = async (_token: string, projectId: string, siteId: string, options?: { archived?: boolean }): Promise<FloorInterface[]> => {
  try {
    const url = new URL(`${API_URL}/data-center/projects/${projectId}/sites/${siteId}/floors`);
    if (options?.archived) {
      url.searchParams.set('archived', 'true');
    }
    const r = await authenticatedFetch(url.toString());

    if (!r.ok) {
      if (r.status === 404) {
        console.log(`No floors found for site ${siteId} in project ${projectId}`);
        return []; // Return empty array for 404
      }
      throw new Error(`HTTP ${r.status}: ${r.statusText}`);
    }

    const data: { results: FloorInterface[] } = await r.json();
    return data.results || [];
  } catch (e: Error | any) {
    if (e.message?.includes('404')) {
      console.log(`No floors found for site ${siteId} in project ${projectId}`);
      return [];
    }
    throw new Error(`Failed to get floors for site ${siteId} in project ${projectId}: ${e.message}`);
  }
}

export const getFloorsById = async (_token: string, id: string): Promise<FloorInterface> => {
  try {
    const r = await authenticatedFetch(`${API_URL}/data-center/floors/${id}`);
    const data: FloorInterface = await r.json();
    return data;
  } catch (e: Error | any) {
    throw new Error(e);
  }
}

export const createFloor = async (_token: string, projectId: string, siteId: string, data: FloorInterface): Promise<FloorInterface> => {
  try {
    const r = await authenticatedFetch(`${API_URL}/data-center/projects/${projectId}/sites/${siteId}/floors`, {
      method: 'POST',
      body: JSON.stringify(data)
    });

    if (!r.ok) {
      throw new Error(r.statusText);
    }
    const res: FloorInterface = await r.json();
    return res;
  } catch (e: Error | any) {
    throw new Error(e);
  }
}

export const updateFloor = async (_token: string, id: string, data: FloorInterface): Promise<FloorInterface> => {
  try {
    const r = await authenticatedFetch(`${API_URL}/data-center/floors/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
    const res: FloorInterface = await r.json();
    return res;
  } catch (e: Error | any) {
    throw new Error(e);
  }
}

export const deleteFloor = async (_token: string, id: string): Promise<void> => {
  try {
    await authenticatedFetch(`${API_URL}/data-center/floors/${id}`, {
      method: 'DELETE'
    });
  } catch (e: Error | any) {
    throw new Error(e);
  }
}

export const getRoomTypes = async (_token: string): Promise<RoomTypeInterface[]> => {
  try {
    const r = await authenticatedFetch(`${API_URL}/data-center/room-types`);
    
    if (!r.ok) {
      throw new Error(`HTTP ${r.status}: ${r.statusText}`);
    }
    
    const data: RoomTypesResponse = await r.json();
    return data.results || [];
  } catch (e: Error | any) {
    console.error('Failed to load room types:', e);
    throw new Error(e.message || 'Failed to load room types');
  }
}

export const getRoomTypeById = async (_token: string, id: string): Promise<RoomTypeInterface> => {
  try {
    const r = await authenticatedFetch(`${API_URL}/data-center/room-types/${id}`);
    const data: RoomTypeInterface = await r.json();
    return data;
  } catch (e: Error | any) {
    throw new Error(e);
  }
}

export const createRoomType = async (_token: string, data: RoomTypeInterface): Promise<RoomTypeInterface> => {
  try {
    const r = await authenticatedFetch(`${API_URL}/data-center/room-types`, {
      method: 'POST',
      body: JSON.stringify(data)
    });
    const res: RoomTypeInterface = await r.json();
    return res;
  } catch (e: Error | any) {
    throw new Error(e);
  }
}

export const updateRoomType = async (_token: string, id: string, data: RoomTypeInterface): Promise<RoomTypeInterface> => {
  try {
    const r = await authenticatedFetch(`${API_URL}/data-center/room-types/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
    const res: RoomTypeInterface = await r.json();
    return res;
  } catch (e: Error | any) {
    throw new Error(e);
  }
}

export const deleteRoomType = async (_token: string, id: string): Promise<void> => {
  try {
    await authenticatedFetch(`${API_URL}/data-center/room-types/${id}`, {
      method: 'DELETE'
    });
  } catch (e: Error | any) {
    throw new Error(e);
  }
}

export const getRoomsBySiteIdAndFloorId = async (_token: string, projectId: string, siteId: string, floorId: string, options?: { archived?: boolean }): Promise<RoomInterface[]> => {
  try {
    const url = new URL(`${API_URL}/data-center/projects/${projectId}/sites/${siteId}/floors/${floorId}/rooms`);
    if (options?.archived) {
      url.searchParams.set('archived', 'true');
    }
    const r = await authenticatedFetch(url.toString());

    if (!r.ok) {
      if (r.status === 404 || r.status === 400) {
        console.log(`No rooms found for floor ${floorId} in site ${siteId} project ${projectId}`);
        return []; // Return empty array for 404/400
      }
      throw new Error(`HTTP ${r.status}: ${r.statusText}`);
    }

    const data: { results: RoomInterface[] } = await r.json();
    return data.results || [];
  } catch (e: Error | any) {
    if (e.message?.includes('404') || e.message?.includes('400')) {
      console.log(`No rooms found for floor ${floorId} in site ${siteId} project ${projectId}`);
      return [];
    }
    throw new Error(`Failed to get rooms for floor ${floorId} in site ${siteId} project ${projectId}: ${e.message}`);
  }
}

export const getRoomsBySiteId = async (_token: string, projectId: string, siteId: string): Promise<RoomInterface[]> => {
  try {
    const r = await authenticatedFetch(`${API_URL}/data-center/projects/${projectId}/sites/${siteId}/rooms`);
    
    if (!r.ok) {
      if (r.status === 404) {
        console.log(`No rooms found for site ${siteId} in project ${projectId}`);
        return []; // Return empty array for 404
      }
      throw new Error(`HTTP ${r.status}: ${r.statusText}`);
    }
    
    const data: { results: RoomInterface[] } = await r.json();
    return data.results || [];
  } catch (e: Error | any) {
    if (e.message?.includes('404')) {
      console.log(`No rooms found for site ${siteId} in project ${projectId}`);
      return [];
    }
    throw new Error(`Failed to get rooms for site ${siteId} in project ${projectId}: ${e.message}`);
  }
}

export const getRoomsById = async (_token: string, projectId: string, roomId: string): Promise<RoomInterface> => {
  try {
    const r = await authenticatedFetch(`${API_URL}/data-center/projects/${projectId}/rooms/${roomId}`);
    
    if (!r.ok) {
      throw new Error(`HTTP ${r.status}: ${r.statusText}`);
    }
    
    const data: RoomInterface = await r.json();
    return data;
  } catch (e: Error | any) {
    throw new Error(`Failed to get room ${roomId} for project ${projectId}: ${e.message}`);
  }
}

export const createRoom = async (_token: string, projectId: string, siteId: string, floorId: string, data: RoomInterface): Promise<RoomInterface> => {
  try {
    const r = await authenticatedFetch(`${API_URL}/data-center/projects/${projectId}/sites/${siteId}/floors/${floorId}/rooms`, {
      method: 'POST',
      body: JSON.stringify(data)
    });
    if (!r.ok) {
      throw new Error(r.statusText);
    }
    const res: RoomInterface = await r.json();
    return res;
  } catch (e: Error | any) {
    throw new Error(e);
  }
}

export const updateRoom = async (_token: string, projectId: string, roomId: string, data: RoomInterface): Promise<RoomInterface> => {
  try {
    console.log('🚀 updateRoom API call started');
    console.log('  - projectId:', projectId);
    console.log('  - roomId:', roomId);
    console.log('  - data:', JSON.stringify(data, null, 2));
    
    const url = `${API_URL}/data-center/projects/${projectId}/rooms/${roomId}`;
    console.log('  - URL:', url);
    
    const requestBody = JSON.stringify(data);
    console.log('  - Request body length:', requestBody.length);
    
    const r = await authenticatedFetch(url, {
      method: 'PUT',
      body: requestBody
    });
    
    console.log('📡 Response received:');
    console.log('  - Status:', r.status);
    console.log('  - OK:', r.ok);
    
    if (!r.ok) {
      console.error('❌ Request failed with status:', r.status);
      let errorText = '';
      try {
        errorText = await r.text();
        console.error('  - Error response body:', errorText);
      } catch (textError) {
        console.error('  - Could not read error response:', textError);
      }
      throw new Error(`HTTP ${r.status}: ${r.statusText}${errorText ? ` - ${errorText}` : ''}`);
    }
    
    const responseText = await r.text();
    console.log('  - Response text length:', responseText.length);
    
    if (!responseText || responseText.trim() === '') {
      console.warn('⚠️  Empty response received, returning original data');
      return data;
    }
    
    try {
      const res: RoomInterface = JSON.parse(responseText);
      console.log('✅ Successfully parsed response:', res);
      return res;
    } catch (parseError) {
      console.error('❌ Failed to parse JSON response:', parseError);
      console.warn('⚠️  Returning original data due to JSON parse error');
      return data;
    }
    
  } catch (e: Error | any) {
    console.error('❌ updateRoom error:', e);
    throw new Error(`Failed to update room ${roomId} for project ${projectId}: ${e.message}`);
  }
}

export const deleteRoom = async (_token: string, projectId: string, roomId: string): Promise<void> => {
  try {
    const r = await authenticatedFetch(`${API_URL}/data-center/projects/${projectId}/rooms/${roomId}`, {
      method: 'DELETE'
    });
    
    if (!r.ok) {
      throw new Error(`HTTP ${r.status}: ${r.statusText}`);
    }
  } catch (e: Error | any) {
    throw new Error(`Failed to delete room ${roomId} for project ${projectId}: ${e.message}`);
  }
}

export const createRow = async (_token: string, projectId: string, siteId: string, roomId: string, data: RowInterface): Promise<RowInterface> => {
  try {
    const r = await authenticatedFetch(`${API_URL}/data-center/projects/${projectId}/sites/${siteId}/rooms/${roomId}/rows`, {
      method: 'POST',
      body: JSON.stringify(data)
    });

    if (!r.ok) {
      throw new Error(r.statusText);
    }

    const res: RowInterface = await r.json();
    return res;
  } catch (e: Error | any) {
    throw new Error(e);
  }
}

export const getRowById = async (_token: string, id: string): Promise<RowInterface> => {
  try {
    const r = await authenticatedFetch(`${API_URL}/data-center/rows/${id}`);
    const data: RowInterface = await r.json();
    return data;
  } catch (e: Error | any) {
    throw new Error(e);
  }
}

export const updateRow = async (_token: string, id: string, data: RowInterface): Promise<RowInterface> => {
  try {
    const r = await authenticatedFetch(`${API_URL}/data-center/rows/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
    const res: RowInterface = await r.json();
    return res;
  } catch (e: Error | any) {
    throw new Error(e);
  }
}

export const deleteRow = async (_token: string, id: string): Promise<void> => {
  try {
    await authenticatedFetch(`${API_URL}/data-center/rows/${id}`, {
      method: 'DELETE'
    });
  } catch (e: Error | any) {
    throw new Error(e);
  }
}

export const getRowRacks = async (_token: string, id: string, page: number, size: number): Promise<RackInterface> => {
  try {
    const r = await authenticatedFetch(`${API_URL}/data-center/rows/${id}/racks?page=${page}&size=${size}`);
    const data = await r.json();
    return data;
  } catch (e: Error | any) {
    throw new Error(e);
  }
}


export const getUnit = async (_token: string, unitId: string) => {
  try {
    const r = await authenticatedFetch(`${API_URL}/units/${unitId}`);
    if (!r.ok) {
      throw new Error(r.statusText);
    }
    const res: RackUnitInterface = await r.json();
    return res;
  } catch (e: Error | any) {
    throw new Error(e);
  }
}

export const getRackUnits = async (_token: string, rackId: string, page: number, size: number) => {
  try {
    const r = await authenticatedFetch(`${API_URL}/racks/${rackId}/units?page=${page}&size=${size}`);
    if (!r.ok) {
      throw new Error(r.statusText);
    }
    const data: RackUnitInterface = await r.json();
    return data;
  } catch (e: Error | any) {
    throw new Error(e);
  }
}

export const createUnit = async (_token: string, projectId: string, siteId: string, rackId: string, unitData: RackUnitInterface) => {
  console.log('🔍 API createUnit: Parameter validation');
  console.log('  - projectId:', projectId || 'MISSING');
  console.log('  - siteId:', siteId || 'MISSING');
  console.log('  - rackId:', rackId || 'MISSING');
  console.log('  - unitData:', unitData);
  
  if (!projectId) throw new Error('Project ID is required');
  if (!siteId) throw new Error('Site ID is required');
  if (!rackId) throw new Error('Rack ID is required');
  
  if (!unitData.rack_id) throw new Error('Unit data must include rack_id');
  if (!unitData.site_id) throw new Error('Unit data must include site_id');
  if (unitData.position === undefined || unitData.position === null) {
    throw new Error('Unit data must include position');
  }
  if (unitData.device_model === undefined || unitData.device_model === null) {
    throw new Error('Unit data must include device_model');
  }
  
  const endpoint = `${API_URL}/data-center/projects/${projectId}/sites/${siteId}/racks/${rackId}/units`;
  
  console.log('🌐 API createUnit: Making request to:', endpoint);
  console.log('📝 API createUnit: Request data:', unitData);
  
  try {
    const r = await authenticatedFetch(endpoint, {
      method: 'POST',
      body: JSON.stringify(unitData)
    });
    
    console.log('📡 API createUnit: Response status:', r.status);
    console.log('📡 API createUnit: Response ok:', r.ok);
    
    if (!r.ok) {
      const errorText = await r.text();
      console.error('❌ API createUnit: Error response:', errorText);
      throw new Error(`HTTP ${r.status}: ${r.statusText} - ${errorText}`);
    }
    
    const res: { id: string } = await r.json();
    console.log('✅ API createUnit: Success response:', res);

    return res;
  } catch (e: Error | any) {
    console.error('❌ API createUnit: Exception:', e);
    throw new Error(e.message || e);
  }
}

export const updateUnit = async (_token: string, projectId: string, unitId: string, unitData: RackUnitInterface) => {
  const endpoint = `${API_URL}/data-center/projects/${projectId}/units/${unitId}`;
  
  console.log('🌐 API updateUnit: Making request to:', endpoint);
  console.log('📝 API updateUnit: Request data:', unitData);
  console.log('🔍 API updateUnit: Parameters:', { projectId, unitId });
  
  try {
    const r = await authenticatedFetch(endpoint, {
      method: 'PUT',
      body: JSON.stringify(unitData)
    });
    
    console.log('📡 API updateUnit: Response status:', r.status);
    console.log('📡 API updateUnit: Response ok:', r.ok);
    
    if (!r.ok) {
      const errorText = await r.text();
      console.error('❌ API updateUnit: Error response:', errorText);
      throw new Error(`HTTP ${r.status}: ${r.statusText} - ${errorText}`);
    }
    
    console.log('✅ API updateUnit: Success');
    return r;
  } catch (e: Error | any) {
    console.error('❌ API updateUnit: Exception:', e);
    throw new Error(e.message || e);
  }
}

export const deleteUnit = async (_token: string, unitId: string) => {
  try {
    const r = await authenticatedFetch(`${API_URL}/units/${unitId}`, {
      method: 'DELETE'
    });
    if (!r.ok) {
      throw new Error(r.statusText);
    }
    return;
  } catch (e: Error | any) {
    throw new Error(e);
  }
}

export const getRack = async (_token: string, rackId: string): Promise<RackInterface> => {
  try {
    const r = await authenticatedFetch(`${API_URL}/racks/${rackId}`);
    if (!r.ok) {
      throw new Error(r.statusText);
    }
    const res: RackInterface = await r.json();
    return res;
  } catch (e: Error | any) {
    throw new Error(e);
  }
}

export const createRack = async (_token: string, projectId: string, siteId: string, rowId: string, rackData: RackInterface) => {
  try {
    const r = await authenticatedFetch(`${API_URL}/data-center/projects/${projectId}/sites/${siteId}/rows/${rowId}/racks`, {
      method: 'POST',
      body: JSON.stringify(rackData)
    });
    if (!r.ok) {
      throw new Error(r.statusText);
    }
    const res: { id: string } = await r.json();
    return res;
  } catch (e: Error | any) {
    throw new Error(e);
  }
}

export const updateRack = async (_token: string, rackId: string, rackData: RackInterface): Promise<void> => {
  try {
    const r = await authenticatedFetch(`${API_URL}/racks/${rackId}`, {
      method: 'PUT',
      body: JSON.stringify(rackData)
    });
    if (!r.ok) {
      throw new Error(r.statusText);
    }
    return;
  } catch (e: Error | any) {
    throw new Error(e);
  }
}

export const deleteRack = async (_token: string, rackId: string): Promise<void> => {
  try {
    const r = await authenticatedFetch(`${API_URL}/racks/${rackId}`, {
      method: 'DELETE'
    });
    if (!r.ok) {
      throw new Error(r.statusText);
    }
    return;
  } catch (e: Error | any) {
    throw new Error(e);
  }
}

export const getRoomsByProjectId = async (_token: string, projectId: string, options?: { archived?: boolean }): Promise<RoomInterface[]> => {
  try {
    console.log(`API call: Getting all rooms for project ${projectId}`);
    const url = new URL(`${API_URL}/data-center/projects/${projectId}/rooms`);
    if (options?.archived) {
      url.searchParams.set('archived', 'true');
    }
    const r = await authenticatedFetch(url.toString());

    if (!r.ok) {
      if (r.status === 404) {
        console.log(`No rooms found for project ${projectId}`);
        return []; // Return empty array for 404
      }
      throw new Error(`HTTP ${r.status}: ${r.statusText}`);
    }

    const data: { results: RoomInterface[] } = await r.json();
    console.log(`API response: Found ${data.results?.length || 0} rooms for project ${projectId}`);
    return data.results || [];
  } catch (e: Error | any) {
    if (e.message?.includes('404')) {
      console.log(`No rooms found for project ${projectId}`);
      return [];
    }
    throw new Error(`Failed to get rooms for project ${projectId}: ${e.message}`);
  }
}

export const updateRoomAccess = async (_token: string, projectId: string, roomId: string, accessData: {
  // Required fields
  site_id: string;
  floor_id: string;
  name: string;
  project_id: string;
  // Optional user-editable fields
  directions?: string;
  access_requirements?: string;
  access_contacts?: string;
  access_contacts_name?: string;
  access_contacts_surname?: string;
  contact_phone?: string;
  contact_email?: string;
}): Promise<void> => {
  try {
    console.log('🏠 updateRoomAccess called with:', {
      projectId,
      roomId,
      accessData: JSON.stringify(accessData, null, 2)
    });

    const r = await authenticatedFetch(`${API_URL}/data-center/projects/${projectId}/rooms/${roomId}`, {
      method: 'PUT',
      body: JSON.stringify(accessData)
    });

    console.log('🏠 updateRoomAccess response:', {
      status: r.status,
      statusText: r.statusText,
      ok: r.ok
    });
    
    if (!r.ok) {
      throw new Error(`HTTP ${r.status}: ${r.statusText}`);
    }
    
    return;
  } catch (e: Error | any) {
    throw new Error(`Failed to update room access for room ${roomId}: ${e.message}`);
  }
}

/**
 * Get rack navigation path - gets all the IDs needed for rack URL
 */
export interface RackNavigationPath {
  projectId: string;
  siteId: string;
  floorId: string;
  roomId: string;
  rowId: string;
  rackId: string;
}

export const getRackNavigationPath = async (_token: string, rackId: string): Promise<RackNavigationPath> => {
  try {
    console.log('🧭 Getting rack navigation path for:', rackId);
    
    const r = await authenticatedFetch(`${API_URL}/racks/${rackId}/navigation-path`);
    
    if (!r.ok) {
      throw new Error(`HTTP ${r.status}: ${r.statusText}`);
    }
    
    const data: RackNavigationPath = await r.json();
    console.log('✅ Rack navigation path loaded:', data);
    return data;
  } catch (e: Error | any) {
    console.error('❌ Error getting rack navigation path:', e);
    throw new Error(`Failed to get rack navigation path: ${e.message}`);
  }
};
